import { ModelVersionType } from '@wrangleworks/wranglesjs'
import { formatBytes, ISODateToLocal } from './misc'
import { TAG_VALIDATION_REGEX, WRANGLE_PURPOSES } from './constants'

export const convertVersionTooltipData = (versions: ModelVersionType[]) => {
  return (
    versions?.map((version) => ({
      title: version?.date_created ? ISODateToLocal(version?.date_created) : '',
      subtitle: version.bytes_used ? `${formatBytes(version.bytes_used)}` : '',
    })) || []
  )
}

export const invalidateQuery = ({ callback }: { callback: Function }) => {
  setTimeout(() => {
    callback()
  }, 500)
}

export const sanitizeBoolean = (value: boolean | string) =>
  value === 'true' || (typeof value === 'boolean' && value)

export const lowerCaseObjectKeys = (obj: object) => {
  if (typeof obj !== 'object' || obj === null) {
    return obj
  }

  if (Array.isArray(obj)) {
    return obj.map(lowerCaseObjectKeys)
  }

  return Object.keys(obj).reduce((acc, key) => {
    const lowerKey = key.toLowerCase()
    acc[lowerKey] = lowerCaseObjectKeys(obj[key])
    return acc
  }, {})
}

export const isObjectsEqual = (obj1: any, obj2: any) => {
  // 1. If both are the same reference or primitive, they are equal
  if (obj1 === obj2) return true

  // 2. If they are different types or one is null, they aren't equal
  if (
    typeof obj1 !== 'object' ||
    obj1 === null ||
    typeof obj2 !== 'object' ||
    obj2 === null
  ) {
    return false
  }

  // 3. Handle Arrays specifically
  if (Array.isArray(obj1) !== Array.isArray(obj2)) return false
  if (Array.isArray(obj1)) {
    if (obj1.length !== obj2.length) return false
    return obj1.every((item, index) => isObjectsEqual(item, obj2[index]))
  }

  // 4. Handle Objects: Check key length first
  const keys1 = Object.keys(obj1)
  const keys2 = Object.keys(obj2)
  if (keys1.length !== keys2.length) return false

  // 5. Recursive check for every key
  return keys1.every((key) => {
    if (!Object.prototype.hasOwnProperty.call(obj2, key)) return false
    return isObjectsEqual(obj1[key], obj2[key])
  })
}

export const normalizeText = (val) => {
  if (typeof val !== 'string') return val
  return val
    .replace(/\r\n/g, '\n') // Standardize newlines
    .replace(/\u00a0/g, ' ') // Replace non-breaking spaces with standard spaces
    .trim()
}

export const getActiveVersionTag = (tags: string[]): string | undefined => {
  if (!tags?.length) return ''
  return tags.find((tag) => TAG_VALIDATION_REGEX.test(tag))
}

export function getWrangleContext(yamlString, cursorLineNumber) {
  if (!yamlString) return null
  const lines = yamlString.split('\n')
  let currentIndex = cursorLineNumber - 1
  const wrangleTypes = Object.values(WRANGLE_PURPOSES)
  const wrangleRegex = /^\s*-\s*([a-zA-Z]+)(?:\.[a-zA-Z0-9_]+)?\s*:/

  while (currentIndex >= 0) {
    const line = lines[currentIndex]
    if (line.trim() === 'wrangles:') {
      return null
    }
    const match = line.match(wrangleRegex)

    if (match) {
      const type = match[1].toLowerCase()
      if (wrangleTypes.includes(type)) {
        return type
      } else {
        return null
      }
    }
    currentIndex--
  }
  return null
}

export const isDev = window.location.origin.includes('dev')
